import {
  parseSlugUrl,
  createEntityUrl,
  generateSlug,
  getCurrentLanguageFromUrl,
} from './urlHelpers';

/**
 * Create a blog post URL using the title and ID with language prefix
 * @param title - The title of the blog post
 * @param postId - The unique ID of the blog post
 * @param lang - Optional language code (defaults to current language)
 * @returns The blog post URL with language prefix
 */
export function createBlogPostUrl(title: string, postId: string, lang?: string): string {
  const currentLang = lang ?? getCurrentLanguageFromUrl();
  const slug = generateSlug(title);
  // Extract first 8 characters of UUID for shorter URL
  const shortId = postId.slice(0, 8);
  return `/${currentLang}/blog/${slug}-${shortId}`;
}

/**
 * Parse a blog post URL to extract the post ID
 * @param urlPath - The URL path (e.g., "my-blog-post-4ec49b47")
 * @returns The post ID or null if not found
 */
export function parseBlogPostUrl(urlPath: string): string | null {
  return parseSlugUrl(urlPath);
}

/**
 * Create a treatment detail URL using the treatment name and ID with language prefix
 * @param treatmentName - The name of the treatment
 * @param treatmentId - The unique ID of the treatment
 * @param lang - Optional language code (defaults to current language)
 * @returns The treatment detail URL with language prefix
 */
export type TreatmentLike = {
  id?: string;
  treatment_id?: string;
  unique_code?: string | null;
  original_name?: string;
  name?: string;
  name_translations?: Record<string, string>;
};

const LANGUAGE_CODE_PATTERN = /^[a-z]{2}$/i;

/**
 * Normalize treatment ID for URL generation (handles UUIDs, prefixed codes, etc.)
 */
function normalizeTreatmentIdForUrl(identifier: string): string {
  const normalizedId = identifier.toString().trim();
  let finalId = '';

  if (normalizedId) {
    const lowerId = normalizedId.toLowerCase();

    // UUID-style IDs: take the first 8 characters before the first hyphen
    if (/^[\da-f]{8}-[\da-f-]+$/.test(lowerId)) {
      finalId = normalizedId.split('-')[0] ?? '';
    }
    // Prefixed codes like TRT-XXXXXX or PROC-XXXXXX: strip the prefix and take up to 8 chars
    else if (/^(trt|proc|treat|med)-[\da-z]+$/i.test(normalizedId)) {
      const match = normalizedId.match(/^([a-z]+)-([\da-z]+)$/i);
      if (match?.[2]) {
        finalId = match[2].slice(0, 8);
      }
    }
    // Already-short codes (<= 12 chars): sanitize and use the first 8
    else if (normalizedId.length <= 12) {
      finalId = normalizedId.replaceAll(/[^\da-z]/gi, '').slice(0, 8);
    }
    // Fallback: remove non-alphanumeric and take the first 8 chars
    else {
      finalId = normalizedId.replaceAll(/[^\da-z]/gi, '').slice(0, 8);
    }
  }

  return finalId;
}

/**
 * Extract treatment name and ID from input parameters
 */
function extractTreatmentName(treatment: TreatmentLike): string {
  return treatment.original_name ?? treatment.name_translations?.en ?? treatment.name ?? '';
}

function extractTreatmentId(treatment: TreatmentLike, fallback?: string): string {
  return treatment.id ?? treatment.unique_code ?? treatment.treatment_id ?? fallback ?? '';
}

function inferLanguageFromId(id: string | undefined, treatmentId: string): string | undefined {
  return typeof id === 'string' && LANGUAGE_CODE_PATTERN.test(id) && treatmentId !== id
    ? id
    : undefined;
}

function extractTreatmentFromObject(
  treatment: TreatmentLike,
  treatmentIdOrLang?: string
): { name: string; id: string; inferredLang: string | undefined } {
  const treatmentName = extractTreatmentName(treatment);
  const treatmentId = extractTreatmentId(treatment, treatmentIdOrLang);
  const inferredLang = inferLanguageFromId(treatmentIdOrLang, treatmentId);

  return { name: treatmentName, id: treatmentId, inferredLang };
}

function extractTreatmentFromString(
  treatmentName: string,
  treatmentIdOrLang?: string
): { name: string; id: string; inferredLang: string | undefined } {
  return {
    name: treatmentName,
    id: treatmentIdOrLang ?? '',
    inferredLang: undefined,
  };
}

function extractTreatmentInfo(
  treatmentOrName: string | TreatmentLike,
  treatmentIdOrLang?: string
): { name: string; id: string; inferredLang: string | undefined } {
  if (typeof treatmentOrName === 'object' && !Array.isArray(treatmentOrName)) {
    return extractTreatmentFromObject(treatmentOrName, treatmentIdOrLang);
  } else if (typeof treatmentOrName === 'string') {
    return extractTreatmentFromString(treatmentOrName, treatmentIdOrLang);
  }

  return { name: '', id: '', inferredLang: undefined };
}

export function createTreatmentUrl(
  treatmentOrName: string | TreatmentLike,
  treatmentIdOrLang?: string,
  lang?: string
): string {
  const {
    name: treatmentName,
    id: treatmentId,
    inferredLang,
  } = extractTreatmentInfo(treatmentOrName, treatmentIdOrLang);

  const currentLang = lang ?? inferredLang ?? getCurrentLanguageFromUrl();
  const slug = generateSlug(treatmentName);
  const finalId = normalizeTreatmentIdForUrl(treatmentId);

  const hasValidId = Boolean(finalId);
  const finalSlug = slug || (hasValidId ? finalId : 'treatment');
  const url = `/${currentLang}/treatment/${finalSlug}-${hasValidId ? finalId : 'item'}`;

  if (hasValidId) {
    return url;
  }

  const fallbackSlug = slug || finalSlug || 'treatment';
  const searchParams = new URLSearchParams();
  searchParams.set('name', fallbackSlug);
  return `${url}?${searchParams.toString()}`;
}

export type EquipmentLike = {
  id?: string;
  equipment_id?: string;
  unique_code?: string;
  code?: string;
  slug?: string | null;
  original_name?: string;
  name?: string;
  name_translations?: Record<string, string>;
};

const normalizeIdentifierForUrl = (value?: string | number | null) => {
  if (value === undefined || value === null) return '';
  return value
    .toString()
    .trim()
    .toLowerCase()
    .replaceAll(/[^\da-z]/g, '');
};

/**
 * Extract equipment name and code from input parameters
 */
function extractEquipmentName(equipment: EquipmentLike): string {
  return equipment.original_name ?? equipment.name_translations?.en ?? equipment.name ?? '';
}

function extractEquipmentCode(equipment: EquipmentLike): string {
  return (
    equipment.unique_code ??
    equipment.code ??
    equipment.slug ??
    equipment.id ??
    equipment.equipment_id ??
    ''
  );
}

function processEquipmentCodeParam(
  param: string | undefined,
  existingCode: string
): { code: string; inferredLang: string | undefined } {
  if (typeof param !== 'string') {
    return { code: existingCode, inferredLang: undefined };
  }

  if (LANGUAGE_CODE_PATTERN.test(param) && existingCode !== param) {
    return { code: existingCode, inferredLang: param };
  }

  return { code: existingCode || param, inferredLang: undefined };
}

function extractEquipmentFromObject(
  equipment: EquipmentLike,
  equipmentCodeOrLang?: string
): { name: string; code: string; inferredLang: string | undefined } {
  const equipmentName = extractEquipmentName(equipment);
  const equipmentCode = extractEquipmentCode(equipment);
  const { code: finalCode, inferredLang } = processEquipmentCodeParam(
    equipmentCodeOrLang,
    equipmentCode
  );

  return { name: equipmentName, code: finalCode, inferredLang };
}

function extractEquipmentFromString(
  equipmentName: string,
  equipmentCodeOrLang?: string
): { name: string; code: string; inferredLang: string | undefined } {
  let equipmentCode = '';
  let inferredLang: string | undefined;

  if (typeof equipmentCodeOrLang === 'string') {
    if (LANGUAGE_CODE_PATTERN.test(equipmentCodeOrLang)) {
      inferredLang = equipmentCodeOrLang;
    } else {
      equipmentCode = equipmentCodeOrLang;
    }
  }

  return { name: equipmentName, code: equipmentCode, inferredLang };
}

function extractEquipmentInfo(
  equipmentOrName: string | EquipmentLike,
  equipmentCodeOrLang?: string
): { name: string; code: string; inferredLang: string | undefined } {
  if (typeof equipmentOrName === 'object' && !Array.isArray(equipmentOrName)) {
    return extractEquipmentFromObject(equipmentOrName, equipmentCodeOrLang);
  } else if (typeof equipmentOrName === 'string') {
    return extractEquipmentFromString(equipmentOrName, equipmentCodeOrLang);
  }

  return { name: '', code: '', inferredLang: undefined };
}

export function createEquipmentUrl(
  equipmentOrName: string | EquipmentLike,
  equipmentCodeOrLang?: string,
  lang?: string
): string {
  const {
    name: equipmentName,
    code: equipmentCode,
    inferredLang,
  } = extractEquipmentInfo(equipmentOrName, equipmentCodeOrLang);

  const currentLang = lang ?? inferredLang ?? getCurrentLanguageFromUrl();
  const slug = generateSlug(equipmentName);
  const normalizedCode = normalizeIdentifierForUrl(equipmentCode);

  const fallbackIdentifier =
    normalizedCode ||
    (typeof equipmentOrName === 'object' && (equipmentOrName.id ?? equipmentOrName.equipment_id)
      ? normalizeIdentifierForUrl(String(equipmentOrName.id ?? equipmentOrName.equipment_id))
      : '');

  const finalIdentifier = fallbackIdentifier || 'item';

  return `/${currentLang}/equipment/${slug}-${finalIdentifier}`;
}

export function parseEquipmentUrl(urlPath: string): {
  identifier: string | null;
  slugName: string | null;
} {
  if (!urlPath) {
    return { identifier: null, slugName: null };
  }

  const trimmed = urlPath.trim().replaceAll(/^\/+|\/+$/g, '');
  if (!trimmed) {
    return { identifier: null, slugName: null };
  }

  const parts = trimmed.split('-');
  if (parts.length < 2) {
    return { identifier: null, slugName: trimmed };
  }

  const identifier = parts.pop() ?? null;
  const slugName = parts.join('-') || null;

  return { identifier, slugName };
}

/**
 * Parse a treatment URL to extract the treatment ID
 * @param urlPath - The URL path (e.g., "liposuction-abc123")
 * @returns The treatment ID or null if not found
 */
export function parseTreatmentUrl(urlPath: string): string | null {
  return parseSlugUrl(urlPath);
}

/**
 * Create a provider detail URL using the provider name and ID with language prefix
 * @param providerName - The name of the provider
 * @param providerId - The unique ID of the provider
 * @param lang - Optional language code (defaults to current language)
 * @returns The provider detail URL with language prefix
 */
export function createProviderUrl(providerName: string, providerId: string, lang?: string): string {
  return createEntityUrl('provider', providerName, providerId, lang);
}

/**
 * Parse a provider URL to extract the provider ID
 * @param urlPath - The URL path (e.g., "gangnam-severance-hospital-abc123")
 * @returns The provider ID or null if not found
 */
export function parseProviderUrl(urlPath: string): string | null {
  return parseSlugUrl(urlPath);
}

/**
 * Create a chat URL using the provider name and ID with language prefix
 * @param providerName - The name of the provider
 * @param providerId - The unique ID of the provider
 * @param lang - Optional language code (defaults to current language)
 * @returns The chat URL with language prefix
 */
export function createChatUrl(providerName: string, providerId: string, lang?: string): string {
  return createEntityUrl('chat', providerName, providerId, lang);
}

/**
 * Parse a chat URL to extract the provider ID
 * @param urlPath - The URL path (e.g., "gangnam-severance-hospital-abc123")
 * @returns The provider ID or null if not found
 */
export function parseChatUrl(urlPath: string): string | null {
  return parseSlugUrl(urlPath);
}

/**
 * Create a doctor detail URL using the doctor name and ID with language prefix
 * @param doctorName - The name of the doctor
 * @param doctorId - The unique ID of the doctor
 * @param lang - Optional language code (defaults to current language)
 * @returns The doctor detail URL with language prefix
 */
export function createDoctorUrl(doctorName: string, doctorId: string, lang?: string): string {
  return createEntityUrl('doctor', doctorName, doctorId, lang);
}

/**
 * Parse a doctor URL to extract the doctor ID
 * @param urlPath - The URL path (e.g., "john-doe-abc123")
 * @returns The doctor ID or null if not found
 */
export function parseDoctorUrl(urlPath: string): string | null {
  return parseSlugUrl(urlPath);
}

/**
 * Create a medical procedure detail URL using the procedure name and ID with language prefix
 * @param procedureName - The name of the medical procedure
 * @param procedureId - The unique ID of the medical procedure
 * @param lang - Optional language code (defaults to current language)
 * @returns The medical procedure detail URL with language prefix
 */
export function createMedicalProcedureUrl(
  procedureName: string,
  procedureId: string,
  lang?: string
): string {
  return createEntityUrl('medical-procedure', procedureName, procedureId, lang);
}

/**
 * Parse a medical procedure URL to extract the procedure ID
 * @param urlPath - The URL path (e.g., "liposuction-abc123")
 * @returns The procedure ID or null if not found
 */
export function parseMedicalProcedureUrl(urlPath: string): string | null {
  return parseSlugUrl(urlPath);
}

/**
 * Create a page URL with language prefix
 * @param pageName - The page name in PascalCase
 * @param lang - Optional language code (defaults to current language)
 * @returns The page URL with language prefix
 */
export function createPageUrl(pageName: string, lang?: string) {
  const currentLang = lang ?? getCurrentLanguageFromUrl();

  // Special case: Home page should be at language root
  if (pageName === 'Home') {
    return `/${currentLang}`;
  }

  // Convert PascalCase to kebab-case: PrivacyPolicy -> privacy-policy
  const kebabCase = pageName
    .replaceAll(/([a-z])([A-Z])/g, '$1-$2') // Insert hyphen between lowercase and uppercase
    .toLowerCase()
    .replaceAll(' ', '-'); // Replace spaces with hyphens

  return `/${currentLang}/${kebabCase}`;
}

export {
  ensureLocale,
  formatCurrency,
  formatCategory,
  formatDateTime,
  formatDate,
  formatSlug,
  formatNumber,
  formatTime,
  normalizeText,
  normalizeLocale,
  normalizeKey,
} from './formatters';
export {
  parseSlugUrl,
  createEntityUrl,
  getCurrentLanguageFromUrl,
  generateSlug,
} from './urlHelpers';
